import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import HeroSection from '../components/HeroSection';

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6 } } };
const stagger = { visible: { transition: { staggerChildren: 0.1 } } };

const faqCategories = [
  {
    category: 'Funding',
    icon: '💰',
    faqs: [
      { q: 'How much funding can I get?', a: 'We offer funding up to $1M depending on your business revenue, time in business, and product type. Apply and get a real number in hours.' },
      { q: 'How fast will I receive funds?', a: 'Most approved businesses receive funds within 24 hours. Some same-day funding is available for qualifying applications submitted before noon.' },
      { q: 'Do I need good credit to qualify?', a: 'No. We evaluate your full business picture — revenue trends, cash flow, and industry — not just your personal credit score. We\'ve funded thousands of businesses with imperfect credit.' },
      { q: 'What do I need to apply?', a: 'Typically: 3 months of bank statements, a government-issued ID, and your business details. No tax returns or extensive documentation required for most products.' },
      { q: 'Are there any hidden fees?', a: 'None. We are 100% transparent about our rates, fees, and terms before you sign anything. What you see is what you get.' },
      { q: 'Can I apply for more funding after repaying?', a: 'Absolutely. Many of our clients return multiple times. Returning clients often qualify for larger amounts and better terms.' },
    ]
  },
  {
    category: 'Partners & ISOs',
    icon: '🤝',
    faqs: [
      { q: 'How do I become a Bizfund partner?', a: 'Fill out our Partner Application on the Partner page. Our ISO team will review your application and reach out within 1 business day to get you onboarded.' },
      { q: 'What commissions do partners earn?', a: 'Our ISO program offers competitive commissions that vary by product and deal size. After onboarding, you\'ll receive full details from your dedicated partner manager.' },
      { q: 'How fast are partner deals funded?', a: 'We prioritize partner-submitted deals. Most are reviewed within hours and funded within 24 hours of approval — keeping your clients happy and your pipeline moving.' },
      { q: 'Do you offer white-label solutions?', a: 'Yes. Qualified ISOs and brokers can access white-label options. Reach out to partners@bizfund.com to learn more about our white-label and co-branded programs.' },
      { q: 'Is there a minimum submission requirement?', a: 'No minimum required to start. We work with new and experienced brokers alike. Volume-based bonus tiers are available once you\'re up and running.' },
    ]
  },
  {
    category: 'General',
    icon: '🔍',
    faqs: [
      { q: 'Is Bizfund a direct lender?', a: 'Yes. We are a direct funder — not a broker. This means faster decisions, more control over your terms, and no middleman adding costs to your deal.' },
      { q: 'Which states do you operate in?', a: 'Bizfund funds businesses in all 50 US states. Some product restrictions may apply depending on state regulations. Contact us for specifics about your state.' },
      { q: 'What industries do you fund?', a: 'We fund virtually every industry — restaurants, retail, construction, medical, transportation, salons, and more. Check our Industries page for a full list.' },
      { q: 'How is Bizfund different from a bank loan?', a: 'We are faster (decisions in hours vs weeks), more flexible (fewer requirements), and designed for small businesses. We don\'t require collateral or years of tax returns for most products.' },
      { q: 'Is my information secure?', a: 'Yes. We use bank-level 256-bit SSL encryption on all data submissions. Your business and personal information is never sold or shared with third parties.' },
      { q: 'How do I contact support?', a: 'Email support@bizfund.com or use the contact form on our Contact page. Our team typically responds within 2 business hours during business days.' },
    ]
  },
];

function FAQCard({ q, a }) {
  return (
    <motion.div variants={fadeUp} className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm transition-shadow hover:shadow-md md:p-6">
      <h4 className="font-bold text-gray-900 mb-3 text-sm leading-snug">{q}</h4>
      <p className="text-sm text-gray-500 leading-relaxed">{a}</p>
    </motion.div>
  );
}

export default function FAQ() {
  return (
    <main>
      <HeroSection
        tag="Frequently Asked Questions"
        title="Got Questions?<br/>We've Got Answers."
        subtitle="Everything you need to know about Bizfund's products, process, and partner program."
        image="/images/offline/photo-1507537297725-24a1c029d3ca.jpg"
      />

      <section className="page-section bg-gray-50">
        <div className="page-container-wide page-stack">
          {faqCategories.map(cat => (
            <div key={cat.category}>
              <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="section-intro">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-2xl">{cat.icon}</span>
                  <h2 className="text-2xl font-bold text-gray-900">{cat.category}</h2>
                </div>
                <div className="h-1 w-12 bg-blue-600 rounded-full ml-11" />
              </motion.div>
              <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }} className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 lg:gap-5">
                {cat.faqs.map(f => <FAQCard key={f.q} {...f} />)}
              </motion.div>
            </div>
          ))}
        </div>
      </section>

      {/* Still Have Questions */}
      <section className="page-section-tight bg-white">
        <div className="page-container max-w-2xl text-center">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Still Have Questions?</h2>
            <p className="text-gray-500 mb-8">Our team is available Monday–Friday, 9am–6pm EST. We typically respond within 2 hours.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/contact" className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-3.5 rounded-full transition-colors">
                Contact Us
              </Link>
              <Link to="/apply" className="cta-primary">
                Apply for Funding
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
